import { Box, Flex, Grid, Text } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import {
  AnonymousHitterAvatar,
  CommonHitterAvatar,
} from 'components/common/hitters/avatar';
import { HitterSideBadge } from 'components/common/hitters/hitter-side-badge';
import { SessionHitsAdvancedFilters } from 'components/sections/analytics/session-hits/advanced-filters';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { IOptionExt } from 'interfaces/forms/option-ext';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { HitHelper } from 'lib_ts/classes/hit.helper';
import { PlateHelper, VISIBLE_STRIKEZONE } from 'lib_ts/classes/plate.helper';
import {
  ANONYMOUS_HITTER_ID,
  HIT_OUTCOME_PRIORITY,
  MAP_OUTCOME_TO_PRETTY_LABEL,
  OutcomeCode,
} from 'lib_ts/enums/hitters.enums';
import { PITCH_TYPE_OPTIONS, PitchType } from 'lib_ts/enums/pitches.enums';
import { IHitter } from 'lib_ts/interfaces/i-hitter';
import { compact, isNil, orderBy } from 'lodash';
import { useCallback, useContext, useMemo } from 'react';
import useHittersStore from 'stores/use-hitters-store';

interface IProps {
  showHitterSelector?: boolean;
  hitterOptions?: IOptionExt[];
  selectedHitter?: IHitter;
  setSelectedHitterId?: (id: string) => void;
}

export const SessionHitsToolbar = ({
  hitterOptions,
  selectedHitter,
  setSelectedHitterId,
}: IProps) => {
  const hits = useAnalyticsStore((s) => s.hits);
  const pitchTypeFilter = useAnalyticsStore((s) => s.pitchTypeFilter);
  const outcomeFilter = useAnalyticsStore((s) => s.outcomeFilter);
  const loading = useAnalyticsStore((s) => s.loadingSessions || s.loadingHits);

  const hitters = useHittersStore((s) => s.hitters);

  const { machine } = useContext(MachineContext);

  const pitchTypeOptions = useMemo(() => {
    const pitchTypes = ArrayHelper.unique(
      hits.map((m) => m.pitch?.type)
    ) as PitchType[];

    return PITCH_TYPE_OPTIONS.filter(
      (v) => v.isGroup || pitchTypes.includes(v.value as PitchType)
    );
  }, [hits]);

  const szDef = useMemo(() => {
    if (!selectedHitter?._id) {
      return VISIBLE_STRIKEZONE;
    }

    const hitter = hitters.find((h) => h._id === selectedHitter?._id);

    if (!hitter || isNil(hitter.height_ft)) {
      return VISIBLE_STRIKEZONE;
    }

    return PlateHelper.getDynamicStrikeZone(
      machine.strikeZoneType,
      hitter.height_ft
    );
  }, [selectedHitter?._id, hitters, machine.strikeZoneType]);

  // limited to what actually exists
  const outcomeOptions = useMemo(() => {
    const allHitOutcomes = ArrayHelper.unique(
      compact(hits.map((h) => HitHelper.getOutcome(h, szDef)))
    );

    const sortedOutcomes = orderBy(allHitOutcomes, (v) =>
      HIT_OUTCOME_PRIORITY.indexOf(v)
    );

    return sortedOutcomes.map((o) => ({
      label: MAP_OUTCOME_TO_PRETTY_LABEL[o],
      value: o,
    }));
  }, [hits, szDef]);

  const showHitterSelector = !!(hitterOptions && hitterOptions.length);

  const renderSelectedHitter = useCallback(() => {
    if (!selectedHitter) {
      return;
    }

    if (!hitterOptions) {
      return;
    }

    if (!hitterOptions.find((o) => o.value === selectedHitter._id)) {
      return;
    }

    return (
      <Flex align="center" gap="2" width="100%">
        {selectedHitter._id === ANONYMOUS_HITTER_ID ? (
          <AnonymousHitterAvatar size="1" />
        ) : (
          <CommonHitterAvatar size="1" hitter={selectedHitter} clickDisabled />
        )}
        <Text truncate>{selectedHitter.name}</Text>
        <HitterSideBadge value={selectedHitter.side} />
      </Flex>
    );
  }, [selectedHitter, hitterOptions]);

  return (
    <ErrorBoundary identifier="SessionHitsToolbar">
      <Flex gap="2">
        <Grid columns={showHitterSelector ? '3' : '2'} gap="2" flexGrow="1">
          {showHitterSelector && (
            <Box>
              <CommonSelectInput
                id="session-hits-hitter-filter"
                name="hitterID"
                options={hitterOptions}
                value={selectedHitter?._id}
                disabled={loading}
                onChange={setSelectedHitterId}
                placeholder="common.hitter"
                renderDisplayValue={renderSelectedHitter}
                className="session-hits-hitter-filter"
                skipSort
              />
            </Box>
          )}
          <Box>
            <CommonSearchInput
              id="pitch-type-filter"
              placeholder={t('common.pitch-type') as string}
              options={pitchTypeOptions}
              values={pitchTypeFilter}
              onChange={(pitchTypes) =>
                useAnalyticsStore.setState({
                  pitchTypeFilter: pitchTypes as PitchType[],
                })
              }
              disabled={loading}
              multiple
              strict
            />
          </Box>

          <Box>
            <CommonSearchInput
              id="outcome-filter"
              placeholder="common.results"
              options={outcomeOptions}
              values={outcomeFilter}
              onChange={(v) =>
                useAnalyticsStore.setState({
                  outcomeFilter: v as OutcomeCode[],
                })
              }
              disabled={loading}
              skipSort
              multiple
            />
          </Box>
        </Grid>
        <Box>
          <SessionHitsAdvancedFilters />
        </Box>
      </Flex>
    </ErrorBoundary>
  );
};
